import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressBarProps } from '@common-fe/common-fe';

export enum ReviewOption {
  CORRECT = 'CORRECT',
  NEEDS_UPDATE = 'NEEDS_UPDATE',
}

export enum ReviewStep {
  REVIEW = 'REVIEW',
  EDIT = 'EDIT',
  RETURNED_ITEMS = 'RETURNED_ITEMS',
}

interface Props {
  canEdit: boolean;
  isAddressEditingAllowed: boolean;
  canOfferPaperless: boolean;
  hasChecks: boolean;
  hasQualifyingLetters: boolean;
  termsAccepted: boolean;
  isConfirming: boolean;
  isProcessingChecks: boolean;
  areCheckDecisionsComplete: boolean;
  closeModal: () => void;
  openModal: () => void;
  confirmAddressIfNeeded: () => Promise<void>;
  processChecksIfNeeded: () => Promise<void>;
  resetChecks: () => void;
  resetTerms: () => void;
  showGoPaperless: () => void;
  hideGoPaperless: () => void;
  showSuccessWithDelay: () => void;
  onSuccessClose: () => void | Promise<void>;
}

const useReviewStepNavigation = ({
  canEdit,
  isAddressEditingAllowed,
  canOfferPaperless,
  hasChecks,
  hasQualifyingLetters,
  termsAccepted,
  isConfirming,
  isProcessingChecks,
  areCheckDecisionsComplete,
  closeModal,
  openModal,
  confirmAddressIfNeeded,
  processChecksIfNeeded,
  resetChecks,
  resetTerms,
  showGoPaperless,
  hideGoPaperless,
  showSuccessWithDelay,
  onSuccessClose,
}: Props) => {
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState<ReviewStep>(ReviewStep.REVIEW);
  const [selectedOption, setSelectedOption] = useState<ReviewOption | null>(null);

  const isEditFlow = useMemo(
    () => selectedOption === ReviewOption.NEEDS_UPDATE && canEdit,
    [selectedOption, canEdit]
  );

  const isReachOutEmployer = useMemo(
    () => selectedOption === ReviewOption.NEEDS_UPDATE && !isAddressEditingAllowed,
    [selectedOption, isAddressEditingAllowed]
  );

  const stepperOptions = useMemo<ProgressBarProps['steps']>(() => {
    const options: ProgressBarProps['steps'] = [{ key: ReviewStep.REVIEW, value: t('Review address') }];
    if (isEditFlow) {
      options.push({ key: ReviewStep.EDIT, value: t('Edit address') });
    }
    options.push({ key: ReviewStep.RETURNED_ITEMS, value: t('Returned Items') });
    return options;
  }, [isEditFlow, t]);

  const resetToReview = useCallback(() => setActiveStep(ReviewStep.REVIEW), []);

  const handleClose = useCallback(() => {
    closeModal();
    setActiveStep(ReviewStep.REVIEW);
    setSelectedOption(null);
    resetChecks();
    resetTerms();
  }, [closeModal, resetChecks, resetTerms]);

  const handleFinish = useCallback(async () => {
    if (isReachOutEmployer) {
      if (hasChecks) {
        await processChecksIfNeeded();
        closeModal();
        setActiveStep(ReviewStep.REVIEW);
        showSuccessWithDelay();
        return;
      }
      handleClose();
      return;
    }

    if (canOfferPaperless) {
      closeModal();
      showGoPaperless();
      return;
    }

    if (selectedOption === ReviewOption.CORRECT) {
      await confirmAddressIfNeeded();
    }
    await processChecksIfNeeded();
    closeModal();
    setActiveStep(ReviewStep.REVIEW);
    showSuccessWithDelay();
  }, [
    isReachOutEmployer,
    hasChecks,
    handleClose,
    canOfferPaperless,
    closeModal,
    showGoPaperless,
    selectedOption,
    confirmAddressIfNeeded,
    processChecksIfNeeded,
    showSuccessWithDelay,
  ]);

  const handleNext = useCallback(async () => {
    if (activeStep === ReviewStep.REVIEW) {
      setActiveStep(isEditFlow ? ReviewStep.EDIT : ReviewStep.RETURNED_ITEMS);
      return;
    }
    if (activeStep === ReviewStep.RETURNED_ITEMS) {
      await handleFinish();
    }
  }, [activeStep, isEditFlow, handleFinish]);

  const handleEditSuccess = useCallback(() => {
    setActiveStep(ReviewStep.RETURNED_ITEMS);
  }, []);

  const handleBack = useCallback(() => {
    resetTerms();
    if (activeStep === ReviewStep.RETURNED_ITEMS && isEditFlow) {
      setActiveStep(ReviewStep.EDIT);
      return;
    }
    setActiveStep(ReviewStep.REVIEW);
  }, [activeStep, isEditFlow, resetTerms]);

  const handleGoPaperlessBack = useCallback(() => {
    hideGoPaperless();
    setActiveStep(ReviewStep.RETURNED_ITEMS);
    openModal();
  }, [hideGoPaperless, openModal]);

  const handleGoPaperlessSubmit = useCallback(async () => {
    if (selectedOption === ReviewOption.CORRECT) {
      await confirmAddressIfNeeded();
    }
    await processChecksIfNeeded();
    hideGoPaperless();
    setActiveStep(ReviewStep.REVIEW);
    showSuccessWithDelay();
  }, [selectedOption, confirmAddressIfNeeded, processChecksIfNeeded, hideGoPaperless, showSuccessWithDelay]);

  const handleSuccessClose = useCallback(async () => {
    setSelectedOption(null);
    resetChecks();
    resetTerms();
    await onSuccessClose();
  }, [resetChecks, resetTerms, onSuccessClose]);

  const isTermsStepBlocked = useMemo(
    () => activeStep === ReviewStep.RETURNED_ITEMS
      && !isReachOutEmployer
      && hasQualifyingLetters
      && !termsAccepted,
    [activeStep, isReachOutEmployer, hasQualifyingLetters, termsAccepted]
  );

  const isNextDisabled = useMemo(() => {
    if (activeStep === ReviewStep.REVIEW) {
      return !selectedOption;
    }
    if (activeStep === ReviewStep.RETURNED_ITEMS) {
      return isConfirming || isProcessingChecks || isTermsStepBlocked || !areCheckDecisionsComplete;
    }
    return false;
  }, [
    activeStep,
    selectedOption,
    isConfirming,
    isProcessingChecks,
    isTermsStepBlocked,
    areCheckDecisionsComplete,
  ]);

  const nextButtonLabel = useMemo(() => {
    if (isReachOutEmployer && !hasChecks && activeStep === ReviewStep.RETURNED_ITEMS) {
      return t('Close');
    }
    return t('Next');
  }, [isReachOutEmployer, hasChecks, activeStep, t]);

  const stepTitle = useMemo(() => {
    if (activeStep === ReviewStep.EDIT) {
      return t('Edit Address');
    }
    if (activeStep === ReviewStep.RETURNED_ITEMS) {
      return t('Returned Items');
    }
    return t('Review Address');
  }, [activeStep, t]);

  return {
    activeStep,
    selectedOption,
    isEditFlow,
    isReachOutEmployer,
    stepperOptions,
    isNextDisabled,
    nextButtonLabel,
    stepTitle,
    setSelectedOption,
    resetToReview,
    handleNext,
    handleBack,
    handleClose,
    handleEditSuccess,
    handleGoPaperlessBack,
    handleGoPaperlessSubmit,
    handleSuccessClose,
  };
};

export default useReviewStepNavigation;
